import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as dates from '@simplybenefits/sb-dates';
import {
    i18n,
    tokens,
    MessageCard,
    Button, 
    Text, 
    TextInput, 
    Theme, 
    BookletDownload, 
    PersonalInfoBlock, 
    EmployeeModalHandler,
    MemberActionsMenu,
    WaiveCoverageModal,
    ReinstateCoverageModal,
    TerminateEmployeeModal,
    SendPasswordResetModal,
    Loader,
    Services
} from '@simplybenefits/sb-ui-library';

import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import { 
    Payment as PaymentIcon,
    ListAlt as ListAltIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { globalStyles } from '../../../theme';
import JourneyBlock from './JourneyBlock.js';
import JourneyTable from './JourneyTable.js';
import NameTag from './NameTag.js';

import Notes from './Notes.js';
import {
    addMemberNote,
    editUser,
    terminateUser,
    resetPassword,
    setClaimsBlockedStatus,
    updateAllBenefitPremiums
} from '../../../actions/users.js';
import * as notifier from '../../../actions/notifier.js';
import { getPdf } from '../../../actions/master.js';
import PermissionsComponent from '../../../components/PermissionsComponent.js';
import BeneficiaryMessageCard from '../../../components/MessageCards/BeneficiaryMessageCard.js';
import { styled } from '@mui/system';
import { getAccount } from '../../../actions/accounts.js';
import BlockMemberClaimsModal from '../Modals/BlockMemberClaimsModal.js';
import permissionLevel from '../../../services/permissions.js';
import BenefitTilesHandler from './BenefitTilesHandler/BenefitTilesHandler.js';
import BenefitActionModal from './BenefitTilesHandler/BenefitActionModal.js';
import getBenefitEligibility from './util/benefitEligibility.js';

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main
}));

class Employee extends Component {
    state = {
        viewAllJourney: false,
        showEditMember: false,
        loading: false,
        showAddNote: false,
        note: '',
        showResources: false,
        showReinstateCoverageModal: false,
        showWaiveCoverageModal: false,
        showMagicLinkModal: false
    };

    componentDidMount = async () => {
        await this.props.dispatch(getAccount(this.props.employee.meta.accountID));
    };

    _viewAllJourney = () => {
        this.setState({
            viewAllJourney: !this.state.viewAllJourney
        });
    };

    _toggleEditMember = (state) => {
        this.setState({
            showEditMember: state
        });
    };

    _showAddNote = () => {
        this.setState({
            showAddNote: true
        });
    };

    _hideAddNote = () => {
        this.setState({
            showAddNote: false
        });
    };

    _addNote = async () => {
        const success = await this.props.dispatch(addMemberNote(this.props.employee.meta.userID, this.state.note));

        if (success) {
            this.setState({
                showAddNote: false,
                note: ''
            });
        }
    };

    _handleChange = (field, value) => {
        this.setState({
            [field]: value
        });
    };

    _generateMemberCard = async (employee) => {
        const fileName = `Benefits_Card.pdf`;
        await this.props.dispatch(getPdf("MyCard", fileName, { userID: employee.userID }));
    };

    _generateProofOfInsurance = async (employee) => {
        const fileName = `Simply_Benefits_Insurance_Proof_${ employee.firstname }_${ employee.lastname }.pdf`;
        await this.props.dispatch(getPdf("ProofOfInsurance", fileName, { userID: employee.userID }));
    };

    _closeResources = () => {
        this.setState({
            showResources: false
        });
    };

    _resetPassword = async () => {
        if (this.state.status === 0 || this.state.status === 'Pending') {
            await this.props.dispatch(notifier.displayError({
                verbiage: 'This user has not yet enrolled. Password cannot be reset, and password reset email has not been sent.'
            }));

            this.setState({ showResetPasswordModal: false });

            return;
        }

        const success = await this.props.dispatch(resetPassword({ email: this.props.employee.meta.email }));

        if (success) {
            this.setState({ showResetPasswordModal: false });
        }
    };

    _validateBenefitActionEffDate = async () => {
        const effectiveDateErrorVerbiage = await Services.validateInput('effectiveDate', this.state.benefitActionEffDate);
        this.setState({ effectiveDateErrorVerbiage });
            
        if (effectiveDateErrorVerbiage) {
            return false;
        }
            
        return true;
    };
    
    _updateBenefitPremiums = async () => {
        const { benefitActionEffDate, benefitPremiumAction } = this.state;
        const effectiveDateTimeStamp = dates.formatOutgoingDate(benefitActionEffDate); 
        const success = await this.props.dispatch(updateAllBenefitPremiums(this.props.employee.meta.userID, effectiveDateTimeStamp, benefitPremiumAction));

        if (success) {
            this.setState({
                benefitActionEffDate: null,
                benefitPremiumAction: null,
                showBenefitActionModal: false
            });

            return true;
        }

        return false;
    };

    render() {
        const { employee, loader } = this.props;

        const styles = {
            container: globalStyles.containerControlSite,
            content: globalStyles.content,
            contentMargin: globalStyles.contentMargin,
            top: {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                margin: "0 0 20px 0",
                flexWrap: 'wrap'
            },
            items: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row'
            },
            titleBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center'
            },
            downloadBtnContainer: {
                display: 'flex',
                flex: 1,
                justifyContent: 'flex-end',
                marginRight: 15
            }
        };

        const user = employee?.meta;
        const whitelabel = employee?.whitelabel;
        const coverage = employee?.coverage;
        const showClaimsBlockedMessage = employee?.meta?.accountClaimsBlocked || employee?.meta?.divisionClaimsBlocked || employee?.meta?.memberClaimsBlocked;
        const hasClaimsBlockingPermission = permissionLevel('Block Claims', this.props.accessType).includes('write');
        const isSpendingAccountOnly = employee?.meta?.isSpendingAccountOnly === 1;
        let dependentColorIndex = 1;
        let beneficiaryColorIndex = 1;

        const { 
            showReinstatePremiumsOption, 
            showWaivePremiumsOption 
        } = getBenefitEligibility(coverage, user);

        if (!this.props.account.meta) {
            return <Loader />;
        }

        return (
            <div>
                {
                    showClaimsBlockedMessage
                        ? (
                            <MessageCard
                                title={i18n.t(tokens.titles.claimsBlocked)}
                                subtitle={"This member’s claims are currently blocked. This includes claim adjudication and claims submissions from all users and sources."}
                                isError={true}
                            />
                        )
                        : null
                }

                <BeneficiaryMessageCard
                    user={ employee?.meta }
                    hideBeneficiaryNotRequired={ true }
                />

                <div style={ styles.titleBlock }>
                    <Text
                        largest
                        veryBold
                        color={ Theme.colors.mainTextGrey }
                        margin={ "10px 0 20px 0" }
                        customStyle={{ flex: '1' }}
                    >
                        { `${ employee.meta.firstname } ${ employee.meta.lastname }` }
                    </Text>

                    <MemberActionsMenu 
                        waiveCoverage={() => this.setState({ showWaiveCoverageModal: true })}
                        showWaiveCoverageOption={ !isSpendingAccountOnly }
                        reinstateCoverage={() => this.setState({ showReinstateCoverageModal: true })}
                        showReinstateCoverageOption={ !isSpendingAccountOnly && (employee.meta.waiveFor || employee.meta.hasWaived) }
                        terminate={() => this.setState({ showTerminateMemberModal: true })}
                        sendPasswordReset={() => this.setState({ showResetPasswordModal: true })}
                        isMasterSite={true}
                        isPasswordSet={employee.meta.isPasswordSet}
                        claimsBlocked={showClaimsBlockedMessage}
                        blockClaims={() => hasClaimsBlockingPermission ? this.setState({ showBlockClaimsModal: true }) : null}
                        unBlockClaims={() => hasClaimsBlockingPermission ? this.setState({ showBlockClaimsModal: true }) : null}
                        waiveAllPremiums={showWaivePremiumsOption ? () => this.setState({ benefitPremiumAction: 'WAIVE_ALL_PREMIUMS', showBenefitActionModal: true }) : null}
                        reinstateAllPremiums={showReinstatePremiumsOption ? () => this.setState({ benefitPremiumAction: 'REINSTATE_ALL_PREMIUMS', showBenefitActionModal: true }) : null}
                    />

                    {
                        Boolean(employee.meta.cert) &&
                            <>
                                <Button
                                    color="primary"
                                    customStyle={{
                                        height: 50,
                                        width: 220,
                                        marginLeft: "10px",
                                        textTransform: "capitalize",
                                        fontSize: 16,
                                        alignSelf: 'flex-end',
                                        marginBottom: "20px"
                                    }}
                                    onClick={() => {
                                        this.setState({
                                            showResources: true
                                        });
                                    }}
                                >
                                            Download Resources
                                </Button>
                            </>
                    }
                </div>

                {
                    this.state.showResources &&
                        <Dialog sx={{ "& .MuiPaper-rounded": { borderRadius: "8px", minWidth: "320px" } }} onClose={this._closeResources} aria-labelledby="simple-dialog-title" open={Boolean(open)}>
                            <DialogTitle id="simple-dialog-title" style={{ paddingBottom: 5, fontFamily: Theme.fonts.type.heading }}>Select Download</DialogTitle>
                            <List>
                                <BookletDownload
                                    employee={ employee }
                                    whitelabel={ whitelabel }
                                    loader={ loader }
                                    closeDialog={ this._closeResources }
                                    getPdf={ (...params) => this.props.dispatch(getPdf(...params)) }
                                    isMasterSite={ true }
                                />

                                <ListItem button onClick={() => this._generateProofOfInsurance(user)} sx={{ padding: "15px 25px" }}>
                                    <ListItemAvatar>
                                        <StyledAvatar>
                                            <ListAltIcon />
                                        </StyledAvatar>
                                    </ListItemAvatar>

                                    <ListItemText primary="Download Proof of Insurance" />
                                </ListItem>

                                {
                                    !isSpendingAccountOnly
                                        ? (
                                            <ListItem button onClick={() => this._generateMemberCard(user)} sx={{ padding: "15px 25px" }}>
                                                <ListItemAvatar>
                                                    <StyledAvatar>
                                                        <PaymentIcon />
                                                    </StyledAvatar>
                                                </ListItemAvatar>

                                                <ListItemText primary="Download Benefits Card" />
                                            </ListItem>
                                        )
                                        : null
                                }

                            </List>
                        </Dialog>

                }

                <div style={ styles.top }>
                    {
                        this.state.showEditMember &&
                            <EmployeeModalHandler
                                open={this.state.showEditMember}
                                isEdit={true}
                                close={() => this._toggleEditMember(false)}
                                currentEmployee={this.props.meta}
                                classData={employee.classes}
                                divisions={employee.divisions}
                                currentAccountMeta={this.props.account.meta}
                                updateUser={(...params) => this.props.dispatch(editUser(...params)) }
                                isMasterSite={true}
                                loader={this.props.loader}
                                isMasterProfile={true}
                            />
                    }

                    <PersonalInfoBlock
                        meta={ employee.meta }
                        coverage={ employee.coverage }
                        classData={ employee.classes }
                        divisions={ employee.divisions }
                        onEdit={() => this._toggleEditMember(true)}
                    />

                    <JourneyBlock
                        journey={ employee.journey }
                        meta={employee.meta}
                        onViewAll={ this._viewAllJourney }
                        viewAllJourney={this.state.viewAllJourney}
                    />
                </div>

                {
                    this.state.viewAllJourney &&
                        <div style={{ marginBottom: 20 }}>
                            <Text
                                extraLarge
                                veryBold
                                color={ Theme.colors.mainTextGrey }
                                margin="0 0 10px 0"
                            >
                                Journey
                            </Text>

                            <JourneyTable journey={ employee.journey } meta={ employee.meta } />
                        </div>
                }

                <div>
                    <Text
                        extraLarge
                        veryBold
                        color={ Theme.colors.mainTextGrey }
                        margin="0 0 10px 0"
                    >
                            Benefits
                    </Text>

                    <BenefitTilesHandler
                        coverage={coverage}
                        user={user}
                    />
                </div>
                
                <PermissionsComponent
                    permission="Notes"
                    accessType={this.props.accessType}
                    level="write"
                >
                    <div style={{ margin: "20px 0 20px 0" }}>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }} onClick={ this._showAddNote }>
                            <Text
                                extraLarge
                                veryBold
                                color={ Theme.colors.mainTextGrey }
                                margin="0 0 10px 0"
                            >
                                Notes
                            </Text>

                            <AddIcon
                                style={{
                                    marginBottom: 8,
                                    fontSize: 30
                                }}
                            />
                        </div>

                        {
                            this.state.showAddNote &&
                                <div>
                                    <TextInput
                                        fullWidth
                                        value={ this.state.note || '' }
                                        variant="outlined"
                                        onChange={(e) => this._handleChange("note", e.target.value)}
                                        multiline={ true }
                                        rows={ 5 }
                                        backgroundColor="#FFFFFF"
                                        placeholder="Add your note here..."
                                        margin="0 0 0 0"
                                    />

                                    <div style={{
                                        display: 'flex',
                                        flex: 1,
                                        alignItems: 'flex-start',
                                        justifyContent: 'flex-end',
                                        marginBottom: 10
                                    }}>
                                        <Button transparent onClick={ this._hideAddNote }>
                                            Close
                                        </Button>

                                        <Button transparent onClick={ this._addNote }>
                                            Save
                                        </Button>
                                    </div>
                                </div>
                        }

                        <Notes
                            notes={ employee.notes }
                            meta={ employee.meta }
                        />
                    </div>
                </PermissionsComponent>

                <div>
                    <Text
                        extraLarge
                        veryBold
                        color={ Theme.colors.mainTextGrey }
                        margin="0 0 10px 0"
                    >
                        Dependents
                    </Text>

                    {
                        employee.dependents.length === 0 ?
                            <Text
                                medium
                                color={ Theme.colors.mainTextGrey }
                            >
                                Member has not provided any Dependents.
                            </Text>
                            :
                            <div style={ styles.items }>
                                {
                                    employee.dependents.map((dependent) => {
                                        if (dependent.status.toUpperCase() === 'TERMINATED') {
                                            return null;
                                        }

                                        let type = 'light';

                                        if (dependentColorIndex === 1) {
                                            type = 'light';
                                            dependentColorIndex = 2;
                                        } else if (dependentColorIndex === 2) {
                                            type = 'medium';
                                            dependentColorIndex = 3;
                                        } else if (dependentColorIndex === 3) {
                                            type = 'dark';
                                            dependentColorIndex = 1;
                                        }

                                        return (
                                            <NameTag item={ dependent } type={type} doHover={ true } key={dependent.dependentID}/>
                                        );
                                    })
                                }
                            </div>
                    }
                </div>

                <div>
                    <Text
                        extraLarge
                        veryBold
                        color={ Theme.colors.mainTextGrey }
                        margin="20px 0 10px 0"
                    >
                        Beneficiaries
                    </Text>

                    {
                        employee.beneficiaries.primary.length === 0 ?
                            <Text
                                medium
                                color={ Theme.colors.mainTextGrey }
                            >
                                Member has not provided an Beneficiaries.
                            </Text>
                            :
                            <div style={ styles.items }>
                                {
                                    employee.beneficiaries.primary.map((beneficiary) => {
                                        let type = 'medium';

                                        if (beneficiaryColorIndex === 1) {
                                            type = 'medium';
                                            beneficiaryColorIndex = 2;
                                        } else if (beneficiaryColorIndex === 2) {
                                            type = 'dark';
                                            beneficiaryColorIndex = 3;
                                        } else if (beneficiaryColorIndex === 3) {
                                            type = 'light';
                                            beneficiaryColorIndex = 1;
                                        }

                                        return (
                                            <NameTag item={ beneficiary } type={type} doHover={ true } key={beneficiary.beneficiaryID}/>
                                        );
                                    })
                                }
                            </div>
                    }
                </div>

                <div>
                    <Text
                        extraLarge
                        veryBold
                        color={ Theme.colors.mainTextGrey }
                        margin="20px 0 10px 0"
                    >
                        Trustee
                    </Text>

                    {
                        employee.trustee ?
                            <NameTag item={ employee.trustee } type="dark" doHover={ true } />
                            :
                            <Text
                                medium
                                color={ Theme.colors.mainTextGrey }
                            >
                                Member has not provided a Trustee.
                            </Text>

                    }
                </div>

                <span id="user-profile" />

                <WaiveCoverageModal
                    open={this.state.showWaiveCoverageModal}
                    close={() => this.setState({ showWaiveCoverageModal: false }) }
                    waiveCoverage={ (...params) => this.props.dispatch(editUser(...params)) }
                    userData={employee.meta}
                    classData={employee.classes}
                    divisions={employee.divisions}
                    isStudent={ this.props.account.isStudentGroup }
                    isMasterSite={true}
                />

                <ReinstateCoverageModal
                    open={this.state.showReinstateCoverageModal}
                    close={() => this.setState({ showReinstateCoverageModal: false }) }
                    reinstateCoverage={ (...params) => this.props.dispatch(editUser(...params)) }
                    userData={employee.meta}
                    classData={employee.classes}
                    divisions={employee.divisions}
                    isStudent={ this.props.account.isStudentGroup }
                    isMasterSite={true}
                />

                <TerminateEmployeeModal
                    open={this.state.showTerminateMemberModal}
                    currentEmployee={employee.meta}
                    terminate={(...params) => this.props.dispatch(terminateUser(...params))}
                    close={ () => this.setState({ showTerminateMemberModal: false })}
                    loader={this.props.loader}
                />

                <SendPasswordResetModal
                    open={this.state.showResetPasswordModal}
                    close={() => this.setState({ showResetPasswordModal: false })}
                    resetPassword={this._resetPassword}
                    loader={this.props.loader}
                />

                <BlockMemberClaimsModal
                    open={this.state.showBlockClaimsModal}
                    onClose={() => this.setState({ showBlockClaimsModal: false })}
                    blockMemberClaims={ (claimsBlocked) => this.props.dispatch(setClaimsBlockedStatus(employee.meta.userID, claimsBlocked)) }
                    member={user}
                    loader={this.props.loader}
                    claimsBlocked={Boolean(showClaimsBlockedMessage)}
                />

                <BenefitActionModal
                    open={this.state.showBenefitActionModal}
                    onClose={() => this.setState({ showBenefitActionModal: false })}
                    onConfirm={this._updateBenefitPremiums}
                    effectiveDate={this.state.benefitActionEffDate}
                    setEffectiveDate={(value) => this.setState({ benefitActionEffDate: value })}
                    effectiveDateError={this.state.effectiveDateErrorVerbiage}
                    validateEffectiveDate={this._validateBenefitActionEffDate}
                    action={this.state.benefitPremiumAction}
                    selectedBenefitName={null}
                    user={user}
                />
            </div>
        );
    }
}

Employee.propTypes = {
    masterLoading: PropTypes.bool,
    accessType: PropTypes.string
};

/**
 * @param state
 * @returns {{loading: boolean}}
 */
const mapStateToProps = (state) => ({
    masterLoading: state.master.loading,
    loader: state.loader,
    accessType: state.master.access,
    account: state.accounts.currentAccount
});

export default connect(mapStateToProps)(Employee);